import type { BookableCandidateDto } from "@repo/api";
import { EventCalendar } from "@repo/ui/components/reui/event-calendar/event-calendar";
import { EventCalendarContent } from "@repo/ui/components/reui/event-calendar/event-calendar-content";
import type {
  CalendarEvent,
  EventCalendarOccurrence,
  EventCalendarSlotInfo,
} from "@repo/ui/components/reui/event-calendar/event-calendar-types";
import { cn } from "@repo/ui/lib/utils";
import { addMinutes, format, startOfDay } from "date-fns";
import { User } from "lucide-react";
import { type MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
// The rail is ONE care manager's day, so no practitioner accent is in play here.
// That frees hue to say role in this booking flow: slate is someone else's
// booking, emerald is a session we have already chosen, primary is the one
// being placed now.
import { timelineBlockSurface } from "@/shared/timeline";
import { SCHEDULE_SLOT_EVENT_CLASS, ScheduleSlotTile } from "../calendar/schedule-slot-card";
import { cursorToStart } from "./cursor-to-start";
import { nearestCandidate } from "./nearest-candidate";
import { spanAvailable } from "./span-available";
import type { RailAppointment, RailBlock, RailDeviceBand, RailSelection } from "./types";
import { useScheduleSessionsContext } from "./use-schedule-sessions-context";

/** A single-resource (care-manager) day timeline on the ReUI event calendar.
 *  Working hours frame the day-grid window; blocked time is hatched and
 *  existing appointments are cards — availability is the absence of both.
 *  Clicking open time snaps to the nearest candidate; the placed session
 *  renders as a read-only schedule tile. */
type RailEventData = { appointment: RailAppointment } | { device: RailDeviceBand } | { selection: RailSelection };

type RailEvent = CalendarEvent<RailEventData>;

function hourOf(time: string, fallback: number): number {
  const h = Number(time.split(":")[0]);
  return Number.isFinite(h) ? h : fallback;
}

export function Rail() {
  const { state, actions } = useScheduleSessionsContext();
  const {
    selectedDate,
    candidates,
    placedCandidate,
    busyBands,
    appointments,
    deviceBands,
    selections,
    dayAvailability,
    bounds,
    advancing,
  } = state;
  const durationMin = state.activeSession?.duration ?? 60;
  const hasCandidates = !advancing && candidates.length > 0;

  // Window = exact working hours (no out-of-hours strip to shade). Falls back to
  // 7a–7p when there's no availability, or when it's the always-available sentinel
  // (startTime === endTime, i.e. "00:00"/"00:00") which would otherwise collapse
  // the rail to a single midnight hour.
  const { dayStartHour, dayEndHour } = useMemo(() => {
    const bounded = dayAvailability && dayAvailability.startTime !== dayAvailability.endTime ? dayAvailability : null;
    const startH = bounded ? hourOf(bounded.startTime, 8) : 7;
    const endH = bounded ? hourOf(bounded.endTime, 18) : 19;
    return { dayStartHour: startH, dayEndHour: Math.min(endH + 1, 24) };
  }, [dayAvailability]);

  // Blocked time is a full-width background band, outside event overlap packing.
  // Device bookings, appointments, and confirmed selections remain foreground events.
  const events = useMemo<RailEvent[]>(() => {
    const out: RailEvent[] = [];
    deviceBands.forEach((d, i) => {
      out.push({ id: `device-${i}`, start: d.start, end: d.end, title: d.deviceName, data: { device: d } });
    });
    appointments.forEach((a, i) => {
      out.push({ id: `appointment-${i}`, start: a.start, end: a.end, title: a.title, data: { appointment: a } });
    });
    selections.forEach((s, i) => {
      out.push({ id: `selection-${i}`, start: s.start, end: s.end, title: s.title, data: { selection: s } });
    });
    return out;
  }, [deviceBands, appointments, selections]);

  // A full-duration placement is available unless it starts in the past, overlaps an
  // occupied band, or falls outside the session's ordering bounds. (Candidate presence
  // is gated upstream.) Stable identity across renders that don't touch these
  // dependencies, so the hover-preview memo below doesn't recompute on every
  // unrelated Rail re-render.
  const spanOk = useCallback(
    (start: Date, end: Date): boolean =>
      spanAvailable({
        start,
        end,
        now: new Date(),
        busyBands,
        appointments,
        deviceBands,
        selections,
        bounds,
      }),
    [busyBands, appointments, deviceBands, selections, bounds],
  );

  // Preview and click resolve the same actual candidate.
  const candidateAt = useCallback(
    (date: Date) => {
      if (!hasCandidates || !spanOk(date, addMinutes(date, durationMin))) return null;
      const best = nearestCandidate(
        candidates.map((candidate) => ({ start: new Date(candidate.start), candidate })),
        date,
      )?.candidate;
      return best && spanOk(new Date(best.start), new Date(best.end)) ? best : null;
    },
    [hasCandidates, candidates, spanOk, durationMin],
  );

  function handleSlotClick(slot: EventCalendarSlotInfo, event: MouseEvent) {
    if (slot.allDay || !selectedDate) return;
    // Use the same raw pointer geometry as the preview, before grid rounding.
    const rect = event.currentTarget.getBoundingClientRect();
    const start = cursorToStart({
      y: event.clientY - rect.top,
      columnHeight: rect.height,
      totalMinutes: (dayEndHour - dayStartHour) * 60,
      durationMinutes: durationMin,
      windowStart: addMinutes(startOfDay(selectedDate), dayStartHour * 60),
      hasCandidates,
    });
    const candidate = start ? candidateAt(start) : null;
    if (candidate) actions.placeCandidate(candidate);
  }

  if (!selectedDate) return null;

  return (
    <div className={cn("schedule-rail min-h-0 flex-1 overflow-hidden", advancing && "opacity-40")}>
      <EventCalendar<RailEventData>
        className="h-full [&_[data-ec-native-scroll]]:scroll-fade [&_[data-ec-native-scroll]]:scroll-fade-12 [&_[data-ec-native-scroll]]:scrollbar-none"
        scrollbars="native"
        classNames={{
          event: SCHEDULE_SLOT_EVENT_CLASS,
          timeGrid: "border-t-0",
          timeGridHeader: "hidden",
          allDaySection: "hidden",
          dayColumn: "[--ec-slot-line-color:color-mix(in_oklab,var(--color-border)_45%,transparent)]",
        }}
        renderTimeGutterSlot={({ time, hour, minute }) =>
          minute === 0 && hour > dayStartHour ? format(time, "h a") : ""
        }
        date={selectedDate}
        onDateChange={() => {}}
        view="day"
        onViewChange={() => {}}
        events={events}
        dayStartHour={dayStartHour}
        dayEndHour={dayEndHour}
        interval={15}
        snapDuration={15}
        slotDuration={durationMin}
        // Click-to-place validates the centered candidate; drag-create stays off.
        interactions={{ drag: false, resize: false, selectSlot: false }}
        onSlotClick={handleSlotClick}
        renderEvent={({ occurrence }) => <RailEventView occurrence={occurrence} />}
        renderDayColumnBackground={({ day, boundsStartMin, boundsEndMin }) => (
          <>
            {busyBands.map((block, index) => {
              const dayStart = startOfDay(day).getTime();
              const start = Math.max(boundsStartMin, (block.start.getTime() - dayStart) / 60_000);
              const end = Math.min(boundsEndMin, (block.end.getTime() - dayStart) / 60_000);
              if (end <= start) return null;
              return (
                <div
                  key={`${block.start.getTime()}-${index}`}
                  className="absolute inset-x-0"
                  style={{
                    top: `calc(var(--ec-hour-height) * ${(start - boundsStartMin) / 60})`,
                    height: `calc(var(--ec-hour-height) * ${(end - start) / 60})`,
                  }}
                >
                  <BlockedCard block={block} />
                </div>
              );
            })}
            {placedCandidate && (
              <PlacedSessionBlock
                candidate={placedCandidate}
                day={day}
                boundsStartMin={boundsStartMin}
                boundsEndMin={boundsEndMin}
              />
            )}
            <RailHoverGhost
              day={day}
              boundsStartMin={boundsStartMin}
              boundsEndMin={boundsEndMin}
              durationMin={durationMin}
              hasCandidates={hasCandidates}
              candidateAt={candidateAt}
            />
          </>
        )}
      >
        <EventCalendarContent />
      </EventCalendar>
    </div>
  );
}

/** The hover ghost shares the day-column background with blocked-time bands.
 *  It measures only its own
 *  rendered bounds via a ref (no library-internal selector), and is
 *  pointer-events-none so it never eats the click that places a session. */
function RailHoverGhost({
  day,
  boundsStartMin,
  boundsEndMin,
  durationMin,
  hasCandidates,
  candidateAt,
}: {
  day: Date;
  boundsStartMin: number;
  boundsEndMin: number;
  durationMin: number;
  hasCandidates: boolean;
  candidateAt: (start: Date) => BookableCandidateDto | null;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [hoverStart, setHoverStart] = useState<Date | null>(null);
  const totalMinutes = boundsEndMin - boundsStartMin;
  const dayMs = day.getTime();
  // Memoized on the numeric inputs (not a freshly-constructed Date) so the
  // effect below doesn't see a new dependency identity on every hover-driven
  // re-render and tear down/re-add the window listener each time.
  // biome-ignore lint/correctness/useExhaustiveDependencies: dayMs is the stable proxy for day
  const windowStart = useMemo(() => addMinutes(startOfDay(day), boundsStartMin), [dayMs, boundsStartMin]);

  useEffect(() => {
    function onMove(e: PointerEvent) {
      const el = ref.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      // The listener lives on window (the background layer is pointer-events-none
      // so clicks pass through to the day column beneath), so it fires for pointer
      // moves anywhere in the app — including the sidebar or session list, at a y
      // that happens to fall inside this column's own bounds. Bound x too, or the
      // ghost paints far from the cursor. `pointerleave` on window fires when the
      // pointer leaves the *window*, not this column, so this bounds check — not
      // that listener — is what actually clears the ghost when the cursor wanders.
      if (e.clientX < rect.left || e.clientX > rect.right) {
        setHoverStart(null);
        return;
      }
      const start = cursorToStart({
        y: e.clientY - rect.top,
        columnHeight: rect.height,
        totalMinutes,
        durationMinutes: durationMin,
        windowStart,
        hasCandidates,
      });
      setHoverStart((prev) => (prev?.getTime() === start?.getTime() ? prev : start));
    }
    function onLeave() {
      setHoverStart(null);
    }
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerleave", onLeave);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerleave", onLeave);
    };
  }, [totalMinutes, durationMin, windowStart, hasCandidates]);

  const preview = useMemo(() => {
    if (!hoverStart) return null;
    const candidate = candidateAt(hoverStart);
    const start = candidate ? new Date(candidate.start) : hoverStart;
    const end = candidate ? new Date(candidate.end) : addMinutes(start, durationMin);
    return {
      available: candidate !== null,
      timeLabel: rangeLabel(start, end),
      startMin: (start.getTime() - windowStart.getTime()) / 60_000,
      durationMin: (end.getTime() - start.getTime()) / 60_000,
    };
  }, [hoverStart, durationMin, windowStart, candidateAt]);

  return (
    <div ref={ref} className="pointer-events-none absolute inset-0 z-20">
      {preview && (
        <div
          className="absolute inset-x-0.5 rounded-md bg-background"
          style={{
            top: `calc(var(--ec-hour-height) * ${preview.startMin / 60})`,
            height: `calc(var(--ec-hour-height) * ${preview.durationMin / 60})`,
          }}
        >
          <ScheduleSlotTile
            accent={preview.available ? "emerald" : "red"}
            variant="scheduled"
            title={<span className="text-[11px] font-normal tabular-nums">{preview.timeLabel}</span>}
            time={null}
          />
        </div>
      )}
    </div>
  );
}

/** Dispatch a foreground event to its renderer by kind. */
function RailEventView({ occurrence }: { occurrence: EventCalendarOccurrence<RailEventData> }) {
  const data = occurrence.event.data;
  if (!data) return null;
  if ("appointment" in data) return <AppointmentCard appointment={data.appointment} />;
  if ("device" in data) return <DeviceBandCard band={data.device} />;
  if ("selection" in data) return <SelectionCard selection={data.selection} />;
  return null;
}

/** Explicit start and end times, including minutes and each meridiem. */
function rangeLabel(start: Date, end: Date): string {
  return `${format(start, "h:mm a")} – ${format(end, "h:mm a")}`;
}

/** A required device booked elsewhere — explains why otherwise-free CM time is
 *  unbookable for this session. Amber hatch with a right-aligned device label. */
function DeviceBandCard({ band }: { band: RailDeviceBand }) {
  return (
    <div
      title={`${band.deviceName} — in use elsewhere`}
      className="rail-band-device flex h-full w-full cursor-not-allowed items-center justify-end overflow-hidden border-y border-dashed border-amber-500/70 px-1.5 transition-colors hover:bg-red-500/10"
    >
      <span className="truncate text-[10px] font-semibold text-amber-700 dark:text-amber-300">{band.deviceName}</span>
    </div>
  );
}

/** Blocked / unavailable time (lunch, time off). Grey hatch with a right-aligned
 *  reason label when one exists. */
function BlockedCard({ block }: { block: RailBlock }) {
  return (
    <div
      title={block.label ? `${block.label} — unavailable` : "Unavailable"}
      className="rail-band-blocked flex h-full w-full cursor-not-allowed items-center justify-end overflow-hidden rounded-none px-1.5 transition-colors hover:bg-red-500/10"
    >
      {block.label && (
        <span className="flex items-center gap-1 text-[10px] font-medium text-muted-foreground">
          <User className="size-2.5 shrink-0" strokeWidth={2} />
          <span className="truncate">{block.label}</span>
        </span>
      )}
    </div>
  );
}

/** An existing booked appointment occupying the care manager's day — "Patient ·
 *  Protocol" over "time · practitioner". */
function AppointmentCard({ appointment }: { appointment: RailAppointment }) {
  const heading = appointment.patientName ? `${appointment.patientName} · ${appointment.title}` : appointment.title;
  const sub = [rangeLabel(appointment.start, appointment.end), appointment.practitionerName]
    .filter(Boolean)
    .join(" · ");
  return (
    <div
      title={`${heading}\n${sub}`}
      className={cn(
        "flex h-full w-full flex-col justify-center px-2 py-1 transition-colors hover:bg-slate-200/90 dark:hover:bg-slate-800/80",
        timelineBlockSurface({ accent: "slate", variant: "delivered" }),
      )}
    >
      <span className="truncate text-[11px] font-semibold">{heading}</span>
      <span className="truncate text-[10px] text-muted-foreground">{sub}</span>
    </div>
  );
}

/** One of our own already-chosen sessions in this booking flow, blocking its slot on
 *  the rail — a filled accent card so it reads as "ours", distinct from booked
 *  appointments (slate) and the active pending placement (outlined). */
function SelectionCard({ selection }: { selection: RailSelection }) {
  return (
    <ScheduleSlotTile
      accent="emerald"
      variant="delivered"
      title={selection.title}
      time={rangeLabel(selection.start, selection.end)}
    />
  );
}

/** A static overlay: no event button, selection state, or keyboard interaction. */
function PlacedSessionBlock({
  candidate,
  day,
  boundsStartMin,
  boundsEndMin,
}: {
  candidate: BookableCandidateDto;
  day: Date;
  boundsStartMin: number;
  boundsEndMin: number;
}) {
  const dayStart = startOfDay(day).getTime();
  const start = Math.max(boundsStartMin, (new Date(candidate.start).getTime() - dayStart) / 60_000);
  const end = Math.min(boundsEndMin, (new Date(candidate.end).getTime() - dayStart) / 60_000);
  if (end <= start) return null;
  return (
    <div
      className="pointer-events-none absolute inset-x-0 z-10 px-0.5 [&>div]:border-solid"
      style={{
        top: `calc(var(--ec-hour-height) * ${(start - boundsStartMin) / 60})`,
        height: `calc(var(--ec-hour-height) * ${(end - start) / 60})`,
      }}
    >
      <PlacedEvent candidate={candidate} />
    </div>
  );
}

/** Proposed session with its time range inline. */
function PlacedEvent({ candidate }: { candidate: BookableCandidateDto }) {
  const start = new Date(candidate.start);
  const end = new Date(candidate.end);

  return (
    <ScheduleSlotTile
      accent="primary"
      variant="tentative"
      title={
        <span className="text-[11px]">
          Proposed <span className="ml-1 font-normal text-muted-foreground">{rangeLabel(start, end)}</span>
        </span>
      }
      time={null}
    />
  );
}
